/**
 * Attest — admin-vy för att godkänna anställdas arbetsdagar innan löneexport.
 * Route: /attest (adminOnly)
 *
 * - Grupperar completed workdays per anställd som INTE är godkända
 * - Visar tydligt vilka som är "Klar för attest" (av anställd) vs ej markerade
 * - Batch-godkänn med checkboxar
 * - Häv attest (behöver anledning som audit-note)
 * - Länk till audit-log per arbetsdag
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle,
  ClockCounterClockwise,
  Sparkle,
  UserCheck,
  Warning,
  PencilLine,
  Lock,
  LockOpen,
  Users,
} from "@phosphor-icons/react";
import { AuditLogModal } from "@/components/AuditLogModal";
import { EditWorkdayModal } from "@/components/EditWorkdayModal";

function fmtHHMM(minutes) {
  const m = Number(minutes) || 0;
  if (m < 0) return "0h 00min";
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${String(rest).padStart(2, "0")}min`;
}

function firstDayOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

// Formattera ISO-timestamp till lokal svensk klocktid (HH:MM). Utan detta
// visas UTC-timmen (2h fel under sommar, 1h fel under vinter i Sverige).
function formatLocalTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

export default function Attest() {
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [statusFilter, setStatusFilter] = useState("pending"); // pending | approved | all
  const [workdays, setWorkdays] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [wds, us] = await Promise.all([
        api.get(`/workday?from_date=${dateFrom}&to_date=${dateTo}`),
        api.get("/users"),
      ]);
      // Bara completed dagar går att attestera
      let list = (wds.data || []).filter((w) => w.status === "completed");
      if (statusFilter === "pending") list = list.filter((w) => !w.approved_at);
      if (statusFilter === "approved") list = list.filter((w) => !!w.approved_at);
      setWorkdays(list);
      setUsers(us.data || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte hämta arbetsdagar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const w of workdays) {
      if (!map.has(w.user_id)) map.set(w.user_id, []);
      map.get(w.user_id).push(w);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.stamp_in > b.stamp_in ? -1 : 1));
    }
    return Array.from(map.entries()).map(([uid, list]) => {
      const u = users.find((x) => x.user_id === uid) || {};
      const netMinutes = list.reduce((s, w) => s + (w.net_minutes || 0), 0);
      const readyCount = list.filter(
        (w) => w.employee_marked_done_at && !w.approved_at
      ).length;
      return { user_id: uid, user: u, days: list, netMinutes, readyCount };
    });
  }, [workdays, users]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPending = () => {
    const pendings = workdays.filter((w) => !w.approved_at).map((w) => w.workday_id);
    setSelected(new Set(pendings));
  };

  const selectAllReady = () => {
    const ready = workdays
      .filter((w) => w.employee_marked_done_at && !w.approved_at)
      .map((w) => w.workday_id);
    setSelected(new Set(ready));
  };

  const clearSelection = () => setSelected(new Set());

  const approveSelected = async () => {
    if (selected.size === 0) return;
    // Skydd: >20 dagar är irreversibelt utan häv-attest + triggar lönespec-mail
    if (selected.size > 20) {
      const ok = window.confirm(
        `Du är på väg att godkänna ${selected.size} arbetsdagar. Detta låser dagarna från redigering och skickar automatiskt lönespec till berörda anställda. Fortsätt?`
      );
      if (!ok) return;
    }
    setProcessing(true);
    try {
      const r = await api.post("/workday/approve", {
        workday_ids: Array.from(selected),
      });
      const n = r.data.approved_count;
      const skipped = r.data.skipped_count || 0;
      const suffix = skipped > 0 ? ` (${skipped} hoppade över — redan godkända eller ej klara)` : "";
      toast.success(
        n === 1
          ? `1 arbetsdag godkänd${suffix}`
          : `${n} arbetsdagar godkända${suffix}`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte godkänna");
    } finally {
      setProcessing(false);
    }
  };

  const unapproveSelected = async () => {
    if (selected.size === 0) return;
    const reason = window.prompt(
      "Anledning till att häva attest? (skrivs i revisionsloggen)"
    );
    if (reason === null) return;
    setProcessing(true);
    try {
      const r = await api.post("/workday/unapprove", {
        workday_ids: Array.from(selected),
        reason: reason.trim() || null,
      });
      const n = r.data.unapproved_count;
      toast.success(
        n === 1
          ? "1 arbetsdag avattesterad"
          : `${n} arbetsdagar avattesterade`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte häva attest");
    } finally {
      setProcessing(false);
    }
  };

  const totalNet = workdays.reduce((s, w) => s + (w.net_minutes || 0), 0);
  const readyForApproval = workdays.filter(
    (w) => w.employee_marked_done_at && !w.approved_at
  ).length;

  return (
    <div className="space-y-6" data-testid="attest-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl">Attest</h1>
          <p className="text-sm text-text-muted mt-1">
            Godkänn anställdas arbetsdagar innan löneexport. Godkända dagar
            låses från redigering och skrivs till revisionsloggen.
          </p>
        </div>
      </div>

      {/* Filter + toolbar */}
      <div
        className="bg-white border border-app rounded-2xl p-5 shadow-sm space-y-4"
        data-testid="attest-toolbar"
      >
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Från
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="attest-date-from"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Till
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="attest-date-to"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="attest-status-filter"
            >
              <option value="pending">Väntar på attest</option>
              <option value="approved">Godkända</option>
              <option value="all">Alla</option>
            </select>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="tk-btn-primary h-[38px]"
            data-testid="attest-refresh-btn"
          >
            {loading ? "Hämtar..." : "Uppdatera"}
          </button>
          <div className="text-right text-xs text-text-muted">
            <div className="font-heading text-lg text-text-primary tabular-nums">
              {workdays.length}
            </div>
            <div>dagar · {fmtHHMM(totalNet)} netto</div>
          </div>
        </div>

        {statusFilter !== "approved" && workdays.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-app">
            <span className="text-sm text-text-muted">
              {selected.size > 0
                ? `${selected.size} vald${selected.size === 1 ? "" : "a"}`
                : `${readyForApproval} klar${readyForApproval === 1 ? "" : "a"} för attest`}
            </span>
            {selected.size === 0 ? (
              <>
                <button
                  onClick={selectAllReady}
                  disabled={readyForApproval === 0}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs"
                  data-testid="select-ready-btn"
                >
                  <Sparkle size={14} className="inline mr-1" />
                  Välj alla klarmarkerade ({readyForApproval})
                </button>
                <button
                  onClick={selectAllPending}
                  disabled={workdays.filter((w) => !w.approved_at).length === 0}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs"
                  data-testid="select-all-pending-btn"
                >
                  Välj alla ({workdays.filter((w) => !w.approved_at).length})
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={approveSelected}
                  disabled={processing}
                  className="tk-btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                  data-testid="approve-selected-btn"
                >
                  <Lock size={14} />
                  {processing ? "Godkänner..." : `Godkänn ${selected.size}`}
                </button>
                {statusFilter === "approved" || statusFilter === "all" ? (
                  <button
                    onClick={unapproveSelected}
                    disabled={processing}
                    className="tk-btn-outline !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                    data-testid="unapprove-selected-btn"
                  >
                    <LockOpen size={14} />
                    Häv attest
                  </button>
                ) : null}
                <button
                  onClick={clearSelection}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs"
                >
                  Rensa
                </button>
              </>
            )}
          </div>
        )}

        {statusFilter === "approved" && workdays.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-app">
            {selected.size === 0 ? (
              <button
                onClick={() =>
                  setSelected(new Set(workdays.map((w) => w.workday_id)))
                }
                className="tk-btn-outline !py-1.5 !px-3 text-xs"
              >
                Välj alla ({workdays.length})
              </button>
            ) : (
              <>
                <span className="text-sm text-text-muted">
                  {selected.size} vald{selected.size === 1 ? "" : "a"}
                </span>
                <button
                  onClick={unapproveSelected}
                  disabled={processing}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                  data-testid="unapprove-selected-btn-approved-tab"
                >
                  <LockOpen size={14} />
                  Häv attest
                </button>
                <button
                  onClick={clearSelection}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs"
                >
                  Rensa
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Empty */}
      {!loading && grouped.length === 0 && (
        <div
          className="bg-white border border-app rounded-2xl p-10 text-center"
          data-testid="attest-empty"
        >
          <div className="w-14 h-14 rounded-full bg-brand/10 text-brand flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} weight="duotone" />
          </div>
          <h3 className="font-heading text-lg mb-1">
            {statusFilter === "pending"
              ? "Inget att attestera"
              : "Inga arbetsdagar i vald period"}
          </h3>
          <p className="text-sm text-text-muted">
            {statusFilter === "pending"
              ? "Alla arbetsdagar i perioden är antingen redan godkända eller ännu inte klara."
              : "Ändra filter eller be dina anställda stämpla in."}
          </p>
        </div>
      )}

      {/* Per-user */}
      {grouped.map((g) => (
        <UserGroup
          key={g.user_id}
          data={g}
          selected={selected}
          toggle={toggleSelect}
          onAudit={(wd) => setAuditFor(wd)}
          onEdit={(wd) => setEditFor(wd)}
        />
      ))}

      {auditFor && (
        <AuditLogModal
          workday={auditFor}
          onClose={() => setAuditFor(null)}
        />
      )}
      {editFor && (
        <EditWorkdayModal
          workday={editFor}
          onClose={() => setEditFor(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function UserGroup({ data, selected, toggle, onAudit, onEdit }) {
  const { user, days, netMinutes, readyCount } = data;
  const approvedCount = days.filter((w) => w.approved_at).length;
  return (
    <div
      className="bg-white border border-app rounded-2xl shadow-sm overflow-hidden"
      data-testid={`attest-user-${user.user_id || data.user_id}`}
    >
      <div className="px-5 py-3 bg-app/40 border-b border-app flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center">
            <Users size={16} weight="duotone" />
          </div>
          <div>
            <div className="font-medium">{user.name || "Okänd"}</div>
            <div className="text-xs text-text-muted mt-0.5 flex items-center gap-3 flex-wrap">
              {user.employment_number ? (
                <span>Anst.nr {user.employment_number}</span>
              ) : (
                <span className="text-amber-700 flex items-center gap-1">
                  <Warning size={12} /> Saknar anst.nr
                </span>
              )}
              <span>{days.length} arbetsdagar</span>
              <span className="font-medium text-brand">
                {fmtHHMM(netMinutes)} netto
              </span>
              {readyCount > 0 && (
                <span className="inline-flex items-center gap-1 text-blue-700">
                  <Sparkle size={12} weight="fill" />
                  {readyCount} klar för attest
                </span>
              )}
              {approvedCount > 0 && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle size={12} weight="fill" />
                  {approvedCount} godkänd{approvedCount === 1 ? "" : "a"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs uppercase tracking-wider">
            <tr className="border-b border-app">
              <th className="text-left py-2 pl-4 pr-2 w-10"></th>
              <th className="text-left py-2 pr-3">Datum</th>
              <th className="text-left py-2 pr-3">Arbetsplats</th>
              <th className="text-left py-2 pr-3">In / Ut</th>
              <th className="text-right py-2 pr-3">Brutto</th>
              <th className="text-right py-2 pr-3">Rast</th>
              <th className="text-right py-2 pr-3">Netto</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-right py-2 pr-4">Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {days.map((w) => {
              const isApproved = !!w.approved_at;
              const isReady = !!w.employee_marked_done_at && !isApproved;
              const isEdited = !!w.edited_at;
              return (
                <tr
                  key={w.workday_id}
                  className="border-b border-app/50 last:border-0 hover:bg-app/30"
                  data-testid={`attest-row-${w.workday_id}`}
                >
                  <td className="py-2 pl-4 pr-2">
                    <input
                      type="checkbox"
                      checked={selected.has(w.workday_id)}
                      onChange={() => toggle(w.workday_id)}
                      className="rounded"
                      data-testid={`attest-checkbox-${w.workday_id}`}
                    />
                  </td>
                  <td className="py-2 pr-3 tabular-nums font-medium">
                    {(w.stamp_in || "").slice(0, 10)}
                  </td>
                  <td className="py-2 pr-3 text-text-muted">
                    {w.workplace_name || "—"}
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-text-muted">
                    {formatLocalTime(w.stamp_in)} –{" "}
                    {formatLocalTime(w.stamp_out) || "…"}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {fmtHHMM(w.gross_minutes)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-text-muted">
                    {fmtHHMM(w.deducted_minutes)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums font-medium text-brand">
                    {fmtHHMM(w.net_minutes)}
                  </td>
                  <td className="py-2 pr-3">
                    {isApproved ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                        title={`Godkänd av ${w.approved_by_name || ""} · ${(w.approved_at || "").slice(0, 16)}`}
                      >
                        <Lock size={11} weight="fill" />
                        Godkänd
                      </span>
                    ) : isReady ? (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                        <Sparkle size={11} weight="fill" />
                        Klar för attest
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-text-muted bg-app border border-app rounded-full px-2 py-0.5">
                        <ClockCounterClockwise size={11} />
                        Ej klar
                      </span>
                    )}
                    {isEdited && (
                      <span
                        className="ml-1 inline-flex items-center gap-1 text-xs text-amber-700"
                        title={`Rättad av ${w.edited_by_name || ""} · ${(w.edited_at || "").slice(0, 16)}`}
                      >
                        <PencilLine size={11} />
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      {!isApproved && (
                        <button
                          onClick={() => onEdit(w)}
                          className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                          data-testid={`edit-workday-btn-${w.workday_id}`}
                          title="Rätta arbetsdag"
                        >
                          <PencilLine size={12} />
                          Rätta
                        </button>
                      )}
                      <button
                        onClick={() => onAudit(w)}
                        className="text-xs text-text-muted hover:text-text-primary underline"
                        data-testid={`audit-log-btn-${w.workday_id}`}
                      >
                        Logg
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
